import sortBy from 'lodash/sortBy';
import { useCallback, useState } from 'react';

import {
  ACTIVITIES_COMPONENT_TYPE_AUDIO,
  ACTIVITIES_COMPONENT_TYPE_DOCUMENT,
  ACTIVITIES_COMPONENT_TYPE_EBOOK,
  ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH,
  ACTIVITIES_COMPONENT_TYPE_IMAGE,
  ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE,
  ACTIVITIES_COMPONENT_TYPE_INFO_BOX,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_LINK,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_SLIDESHOW,
  ACTIVITIES_COMPONENT_TYPE_VIDEO,
  ACTIVITIES_COMPONENT_TYPE_WEBTOOL,
  ACTIVITIES_COMPONENT_TYPE_VOCABULARY,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_DRAW,
  ACTIVITIES_COMPONENT_TYPE_TABLE,
  ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE,
  ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION,
  ACTIVITIES_COMPONENT_TYPE_GIF,
} from 'global/constants';
import {
  ComponentAudio,
  ComponentColumn,
  ComponentGuidedResearch,
  ComponentImage,
  ComponentImageSphere,
  ComponentInfoBox,
  ComponentLink,
  ComponentResponse,
  ComponentVideo,
  ComponentColumnsComponents,
  ComponentEquationResponse,
  ComponentSlideshow,
  ComponentMultipleChoice,
  ComponentEbook,
  ComponentDocument,
  ComponentInteractive,
  ComponentEbookInteractive,
  ComponentVocabulary,
  ComponentFillInBlanks,
  ComponentDraw,
  ComponentTable,
  ComponentDiscussion,
  ComponentInteractiveResponseSequence,
  ComponentInteractiveResponseCategorise,
  ComponentRiveAnimation,
  ComponentGif,
} from '../component.constants';
import { ColumnWrapper } from './columns.style';
import InfoBoxView from '../info-box/info-box-view';
import VideoView from '../video/video-view';
import ResponsePrint from '../response/response-print';
import ResponseView from '../response/response-view';
import ImageView from '../image/image-view';
import ImageSphereView from '../image-sphere/image-sphere-view';
import LinkView from '../link/link-view';
import WebToolView from '../webtool/webtool-view';
import GuidedResearchView from '../guided-research/guided-research-view';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import ActivityBuilderZoomComponentModal from 'approot/activity-builder/activity-builder-zoom-component-modal';
import EquationResponseView from '../equation-response/equation-response-view';
import SlideshowView from '../slideshow/slideshow-view';
import MultipleChoiceView from '../multiple-choice/multiple-choice-view';
import EbookView from '../ebook/ebook-view';
import DocumentView from '../document/document-view';
import InteractiveView from '../interactive/interactive-view';
import InteractiveEbookView from '../interactive-ebook/interactive-ebook-view';
import VocabularyView from '../vocabulary/vocabulary-view';
import FillInBlanksView from '../fill-in-blanks/fill-in-blanks-view';
import DrawView from '../draw/draw-view';
import TableView from '../table/table-view';
import DiscussionView from '../discussion/discussion-view';
import InteractiveResponseView from '../interactive-response/sequence/interactive-response-view';
import InteractiveResponseCategoriseView from '../interactive-response/categorise/interactive-response-categorise-view';
import ComponentsViewWrapper from '../components-view-wrapper';
import { useActivityBuilderFlags } from 'approot/activity-builder/activity-builder-flags';
import {
  ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
  ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
  ComponentEventData,
  ComponentExpandModeType,
  pushComponentViewEvent,
} from '../../present/activity-builder-present-data-tracking.utils';
import { EVENT_ACTION_EDIT } from 'approot/shared/data-tracker/data-events.constants';
import RiveView from '../rive/rive-view';
import GifView from '../gif/gif-view';
import { getStyleProperties } from 'approot/activity-builder/components/components.utils';
import {
  getAppliedColour,
  isBackgroundApplied,
  isBorderApplied,
} from 'approot/activity-builder/components/shared/component-settings-borders.utils';

type Props = {
  column: ComponentColumn;
  contentId: number;
  eventTrackingData?: ComponentEventData;
};

const ColumnsComponentsView = ({
  column,
  contentId,
  eventTrackingData,
}: Props) => {
  const { activityBuilderRespondFlag } = useActivityBuilderFlags();

  const sortedComponents = sortBy(column.components, 'sort');

  const [zoomedComponent, setZoomedComponent] = useState<
    ComponentColumnsComponents | undefined
  >();

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const handleOpenModal = useCallback(
    (
      component: ComponentColumnsComponents,
      expandMode?: ComponentExpandModeType
    ) => {
      setZoomedComponent(component);
      pushComponentViewEvent({
        component,
        eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
        expandMode,
        eventTrackingData,
        contentId,
      });
      openModal();
    },
    [contentId, eventTrackingData, openModal]
  );

  return (
    <ColumnWrapper>
      {sortedComponents.map(component => {
        const openModalProps = { openModal: () => handleOpenModal(component) };

        return (
          <ComponentsViewWrapper
            key={component.id}
            type={component.type}
            id={component.id}
            colours={getAppliedColour(getStyleProperties(component))}
            showBorder={isBorderApplied(getStyleProperties(component))}
            showBackground={isBackgroundApplied(getStyleProperties(component))}
          >
            {component.type === ACTIVITIES_COMPONENT_TYPE_INFO_BOX && (
              <InfoBoxView
                {...(component as ComponentInfoBox)}
                {...openModalProps}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_SLIDESHOW && (
              <SlideshowView
                {...(component as ComponentSlideshow)}
                onComponentExpand={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {!activityBuilderRespondFlag &&
              component.type ===
                ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE && (
                <ResponsePrint
                  {...(component as ComponentResponse)}
                  {...openModalProps}
                />
              )}
            {activityBuilderRespondFlag &&
              component.type ===
                ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE && (
                <ResponseView
                  {...(component as ComponentResponse)}
                  {...openModalProps}
                />
              )}

            {component.type ===
              ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE && (
              <MultipleChoiceView {...(component as ComponentMultipleChoice)} />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_EBOOK && (
              <EbookView
                {...(component as ComponentEbook)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_DOCUMENT && (
              <DocumentView
                {...(component as ComponentDocument)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_VIDEO && (
              <VideoView
                {...(component as ComponentVideo)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_AUDIO && (
              <VideoView
                {...(component as ComponentAudio)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE && (
              <ImageView
                {...(component as ComponentImage)}
                {...openModalProps}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE && (
              <ImageSphereView {...(component as ComponentImageSphere)} />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_INTERACTIVE && (
              <InteractiveView
                {...(component as ComponentInteractive)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE && (
              <InteractiveEbookView
                {...(component as ComponentEbookInteractive)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_LINK && (
              <LinkView
                {...(component as ComponentLink)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_WEBTOOL && (
              <WebToolView
                {...(component as ComponentLink)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH && (
              <GuidedResearchView
                {...(component as ComponentGuidedResearch)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE && (
              <EquationResponseView
                {...(component as ComponentEquationResponse)}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_VOCABULARY && (
              <VocabularyView
                {...(component as ComponentVocabulary)}
                onComponentExpand={({ vocabularyWord, vocabularyWordCount }) =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
                    eventTrackingData,
                    contentId,
                    vocabularyWord,
                    vocabularyWordCount,
                  })
                }
              />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE && (
              <FillInBlanksView {...(component as ComponentFillInBlanks)} />
            )}
            {component.type === ACTIVITIES_COMPONENT_TYPE_DRAW && (
              <DrawView
                {...(component as ComponentDraw)}
                {...openModalProps}
                onOpenDrawCanvas={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
                    expandMode: EVENT_ACTION_EDIT,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_TABLE && (
              <TableView {...(component as ComponentTable)} />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE && (
              <DiscussionView
                {...(component as ComponentDiscussion)}
                onComponentExpand={expandMode =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
                    expandMode,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE && (
              <InteractiveResponseView
                {...(component as ComponentInteractiveResponseSequence)}
              />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE && (
              <InteractiveResponseCategoriseView
                {...(component as ComponentInteractiveResponseCategorise)}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION && (
              <RiveView {...(component as ComponentRiveAnimation)} />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_GIF && (
              <GifView {...(component as ComponentGif)} {...openModalProps} />
            )}
          </ComponentsViewWrapper>
        );
      })}

      {isVisible && zoomedComponent && (
        <ActivityBuilderZoomComponentModal
          component={zoomedComponent}
          closeModal={() => {
            setZoomedComponent(undefined);
            closeModal();
          }}
        />
      )}
    </ColumnWrapper>
  );
};

export default ColumnsComponentsView;
